import { ApolloClient, InMemoryCache, HttpLink, makeVar, ApolloLink } from '@apollo/client'
import { LocalState } from '@apollo/client/local-state'
import { decodeCursor, LIMIT } from './cursor'
import { COMMENTS_LIMIT, SSR } from './constants'
import { RetryLink } from '@apollo/client/link/retry'
import { isMutationOperation, isQueryOperation } from '@apollo/client/utilities'

function isFirstPage (cursor, existingThings, limit = LIMIT) {
  if (cursor) {
    const decursor = decodeCursor(cursor)
    return decursor.offset === limit
  } else {
    // we don't have anything cached, or our existing items are less than
    // or equal to a full page
    return existingThings?.length < limit
  }
}

const defaultFetchPolicy = SSR ? 'cache-only' : 'cache-first'
const defaultNextFetchPolicy = SSR ? 'cache-only' : 'cache-first'

export default function getApolloClient () {
  if (SSR) {
    return getClient(`${process.env.SELF_URL}/api/graphql`)
  } else {
    window.apolloClient ||= getClient('/api/graphql')
    return window.apolloClient
  }
}

export const meAnonPiconeros = {}

// preserve locally-bumped zap/boost/downzap counters against stale SSR writes on navigation.
// cache.modify (used by modifyActCache/updateAncestors) bypasses merge, so local bumps write
// through cleanly; merge only runs on writeQuery/query-result writes, which is where stale
// ssrData would otherwise clobber a pending zap that hasn't been sent to the server yet.
const maxMerge = (existing, incoming) => {
  if (existing == null) return incoming
  if (incoming == null) return existing
  return Math.max(existing, incoming)
}

// the Item counters protected by maxMerge: optimistic act bumps (cache.modify, which bypasses
// merge) write through cleanly, and stale SSR/query writes can only raise them, never clobber a
// pending zap. the notifications reconcile link below writes these same fields as server truth.
const ITEM_COUNTER_FIELDS = [
  'piconeros', 'mePiconeros', 'meDontLikePiconeros', 'downPiconeros', 'boost',
  'commentPiconeros', 'commentDownPiconeros', 'commentBoost'
]

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 30000,
    jitter: true
  },
  attempts: {
    max: Infinity,
    retryIf: (error, _operation) => {
      // retry only if the error is not a mutation and the operation is a query
      return !!error && !isMutationOperation(_operation.query) && isQueryOperation(_operation.query)
    }
  }
})

// a failed payment is seen and retried on the notifications feed. for each PayInification
// (failed-payment) notification, write the server's item counters as truth via cache.modify,
// bypassing maxMerge — correcting an optimistic bump currently in the cache.
// Scoping to the payInItem (not every item in the feed) is exact: a failed act only
// ever bumped the acted-on item, so only it can be stale; other notifications' items are untouched.
function reconcileItemCounters (cache, data) {
  if (!data || typeof data !== 'object') return
  if (data.__typename === 'Item' && data.id != null) {
    const fields = {}
    for (const field of ITEM_COUNTER_FIELDS) {
      if (typeof data[field] === 'number') {
        fields[field] = () => data[field]
      }
    }
    if (Object.keys(fields).length > 0) {
      cache.modify({ id: `Item:${data.id}`, fields })
    }
  }
  for (const value of Object.values(data)) {
    reconcileItemCounters(cache, value)
  }
}

// reconcile item counters for the notifications feed at the data-consumption layer.
// the feed is delivered via SSR (writeQuery, subject to maxMerge which can't lower a stale
// optimistic bump) and read cache-first (so no client-link request fires), so this can't live
// in an ApolloLink — it must run wherever the rendered notifications data is available.
// callers pass the displayed list so both the SSR first page and fetchMore pages are covered.
export function reconcileNotificationItemCounters (cache, notifications) {
  for (const n of notifications ?? []) {
    if (n.__typename === 'PayInification' && n.payInItem) {
      reconcileItemCounters(cache, n.payInItem)
    }
  }
}

function getClient (uri) {
  const link = ApolloLink.from([
    retryLink,
    new HttpLink({ uri })
  ])

  return new ApolloClient({
    link,
    ssrMode: SSR,
    devtools: { enabled: process.env.NODE_ENV !== 'production' },
    cache: new InMemoryCache({
      freezeResults: true,
      // https://github.com/apollographql/apollo-client/issues/7648
      possibleTypes: {
        Notification: [
          'Reply',
          'Votification',
          'BountyPayment',
          'Mention',
          'Invitification',
          'PayInification',
          'Earn',
          'JobChanged',
          'Referral',
          'Flame',
          'NewVerified',
          'QuestComplete',
          'FlameDay',
          'FollowActivity',
          'Revenue',
          'SubStatus',
          'TerritoryPost',
          'TerritoryTransfer',
          'Reminder',
          'ItemMention',
          'ReferralReward',
          'Bulletinification'
        ],
        PayInResult: [
          'Item',
          'ItemAct',
          'PollVote',
          'Sub'
        ],
        WalletOrTemplate: [
          'Wallet',
          'WalletTemplate'
        ]
      },
      typePolicies: {
        Sub: {
          keyFields: ['name'],
          fields: {
            optional: {
              merge: true
            }
          }
        },
        SubBranding: {
          keyFields: ['subName']
        },
        PayIn: {
          keyFields: ['id', 'isSend'],
          fields: {
            isSend: {
              read (isSend) {
                return isSend ?? true
              }
            },
            payerPrivates: {
              merge: true
            }
          }
        },
        User: {
          // https://www.apollographql.com/docs/react/caching/cache-field-behavior/#merging-non-normalized-objects
          fields: {
            privates: {
              merge: true
            },
            optional: {
              merge: true
            },
            bio: {
              merge: true
            }
          }
        },
        Wallet: {
          fields: {
            vaultEntries: {
              replace: true
            }
          }
        },
        Query: {
          fields: {
            sub: {
              keyArgs: ['name'],
              merge (existing, incoming) {
                return incoming
              }
            },
            topSubs: {
              keyArgs: ['when', 'by', 'from', 'to', 'limit'],
              merge (existing, incoming, { args }) {
                if (isFirstPage(incoming.cursor, existing?.subs, args.limit)) {
                  return incoming
                }

                return {
                  cursor: incoming.cursor,
                  subs: [...(existing?.subs || []), ...incoming.subs]
                }
              }
            },
            topUsers: {
              keyArgs: ['when', 'by', 'from', 'to', 'limit'],
              merge (existing, incoming, { args }) {
                if (isFirstPage(incoming.cursor, existing?.users, args.limit)) {
                  return incoming
                }

                return {
                  cursor: incoming.cursor,
                  users: [...(existing?.users || []), ...incoming.users]
                }
              }
            },
            mySubscribedUsers: {
              keyArgs: false,
              merge (existing, incoming) {
                if (isFirstPage(incoming.cursor, existing?.users)) {
                  return incoming
                }

                return {
                  cursor: incoming.cursor,
                  users: [...(existing?.users || []), ...incoming.users]
                }
              }
            },
            myMutedUsers: {
              keyArgs: false,
              merge (existing, incoming) {
                if (isFirstPage(incoming.cursor, existing?.users)) {
                  return incoming
                }

                return {
                  cursor: incoming.cursor,
                  users: [...(existing?.users || []), ...incoming.users]
                }
              }
            },
            userSuggestions: {
              keyArgs: ['q', 'limit'],
              merge (existing, incoming) {
                return incoming
              }
            },
            topCowboys: {
              keyArgs: [],
              merge (existing, incoming) {
                if (isFirstPage(incoming.cursor, existing?.users)) {
                  return incoming
                }

                return {
                  cursor: incoming.cursor,
                  users: [...(existing?.users || []), ...incoming.users]
                }
              }
            },
            items: {
              keyArgs: ['sub', 'sort', 'type', 'name', 'when', 'by', 'from', 'to', 'limit'],
              merge (existing, incoming) {
                if (isFirstPage(incoming.cursor, existing?.items)) {
                  return incoming
                }

                return {
                  cursor: incoming.cursor,
                  items: [...(existing?.items || []), ...incoming.items],
                  pins: [...(existing?.pins || []), ...(incoming.pins || [])]
                }
              }
            },
            related: {
              keyArgs: ['id', 'title', 'minMatch', 'limit'],
              merge (existing, incoming, { args }) {
                if (isFirstPage(incoming.cursor, existing?.items, args.limit)) {
                  return incoming
                }

                return {
                  cursor: incoming.cursor,
                  items: [...(existing?.items || []), ...incoming.items]
                }
              }
            },
            search: {
              keyArgs: ['q', 'sort', 'what', 'when', 'from', 'to', 'limit'],
              merge (existing, incoming) {
                if (isFirstPage(incoming.cursor, existing?.items)) {
                  return incoming
                }

                return {
                  cursor: incoming.cursor,
                  items: [...(existing?.items || []), ...incoming.items],
                  searchSuggestion: existing?.searchSuggestion ?? incoming.searchSuggestion
                }
              }
            },
            searchUsers: {
              keyArgs: ['q', 'limit', 'similarity'],
              merge (existing, incoming) {
                return [...(existing?.searchUsers || []), ...incoming]
              }
            },
            notifications: {
              keyArgs: ['inc'],
              merge (existing, incoming) {
                if (isFirstPage(incoming.cursor, existing?.notifications)) {
                  return incoming
                }

                return {
                  cursor: incoming.cursor,
                  earn: existing?.earn || incoming.earn,
                  notifications: [...(existing?.notifications || []), ...incoming.notifications],
                  lastChecked: existing?.lastChecked || incoming.lastChecked
                }
              }
            },
            statistics: {
              keyArgs: ['walletId'],
              merge (existing, incoming) {
                if (isFirstPage(incoming.cursor, existing?.payIns)) {
                  return incoming
                }

                return {
                  cursor: incoming.cursor,
                  payIns: [...(existing?.payIns || []), ...incoming.payIns]
                }
              }
            },
            walletLogs: {
              keyArgs: ['walletId', 'payInId'],
              merge (existing, incoming) {
                return incoming
              }
            },
            failedPayIns: {
              keyArgs: [],
              merge (existing, incoming) {
                return incoming
              }
            }
          }
        },
        Item: {
          fields: {
            ...Object.fromEntries(ITEM_COUNTER_FIELDS.map(field => [field, { merge: maxMerge }])),
            comments: {
              keyArgs: ['sort'],
              merge (existing, incoming) {
                // check if we're not on a paginated item: it shouldn't have an incoming or an already existing cursor,
                // and its incoming comments shouldn't be more than COMMENTS_LIMIT
                // in this case, like isFirstPage, replace the cache
                const isNotPaginated = !incoming.cursor && !existing?.cursor && incoming.comments?.length >= COMMENTS_LIMIT
                if (isFirstPage(incoming.cursor, existing?.comments, COMMENTS_LIMIT) || isNotPaginated) {
                  return incoming
                }

                return {
                  cursor: incoming.cursor,
                  comments: [...(existing?.comments || []), ...incoming.comments]
                }
              }
            },
            live: {
              read (live) {
                return live || false
              }
            },
            meAnonPiconeros: {
              read (existingAmount, { readField }) {
                if (SSR) return null

                const itemId = readField('id')

                // we need to use reactive variables such that updates
                // to local state propagate correctly
                // see https://www.apollographql.com/docs/react/local-state/reactive-variables
                let reactiveVar = meAnonPiconeros[itemId]
                if (!reactiveVar) {
                  const storageKey = `TIP-item:${itemId}`
                  const existingAmount = Number(window.localStorage.getItem(storageKey) || '0')
                  reactiveVar = makeVar(existingAmount || 0)
                  meAnonPiconeros[itemId] = reactiveVar
                }

                return reactiveVar()
              }
            }
          }
        }
      }
    }),
    assumeImmutableResults: true,
    queryDeduplication: true,
    defaultOptions: {
      watchQuery: {
        initialFetchPolicy: defaultFetchPolicy,
        fetchPolicy: defaultFetchPolicy,
        nextFetchPolicy: defaultNextFetchPolicy,
        notifyOnNetworkStatusChange: false,
        ssr: SSR
      },
      query: {
        initialFetchPolicy: defaultFetchPolicy,
        fetchPolicy: defaultFetchPolicy,
        nextFetchPolicy: defaultNextFetchPolicy,
        ssr: SSR
      }
    },
    // @client directive support
    localState: new LocalState({})
  })
}
